import {
  Scene,
  Cursor,
  Tile,
  ActionButton,
  Badge,
} from "views/components/Guide/art/common";

import styles from "./style.module.scss";

const CYCLE_NODES = [
  { cx: 56, cy: 70, label: "Auto-label", sub: "Visual Layer" },
  { cx: 184, cy: 70, label: "Review", sub: "You" },
  { cx: 120, cy: 150, label: "Refine", sub: "Next cycle" },
];

// Three-node loop with a glow that travels auto-label, review, refine.
export const CycleScene = () => (
  <Scene
    label="The label propagation loop: auto-label, review, refine"
    className={styles.reviewScene}
  >
    <path
      className="ga-stroke-primary ga-cycle-path"
      d="M84 60 Q120 38 156 60 M176 98 Q156 136 148 140 M92 140 Q84 136 64 98"
      strokeWidth="1.5"
      strokeDasharray="4 5"
      strokeLinecap="round"
    />
    {CYCLE_NODES.map((node, i) => (
      <g key={node.label}>
        <circle
          className={`ga-fill-primary ga-node-glow ga-node-glow-${i + 1}`}
          cx={node.cx}
          cy={node.cy}
          r="36"
        />
        <circle
          className="ga-surface ga-node"
          cx={node.cx}
          cy={node.cy}
          r="28"
        />
        <circle
          className="ga-stroke-primary"
          cx={node.cx}
          cy={node.cy}
          r="28"
          strokeWidth="1.5"
        />
        <text
          className="ga-text"
          x={node.cx}
          y={node.cy + 1}
          fontSize="8"
          textAnchor="middle"
        >
          {node.label}
        </text>
        <text
          className="ga-text ga-muted"
          x={node.cx}
          y={node.cy + 11}
          fontSize="6.5"
          textAnchor="middle"
        >
          {node.sub}
        </text>
      </g>
    ))}
  </Scene>
);

// Mirrors the review bar order: approve, reassign, ignore, decline.
const ReviewActionBar = ({ className }: { className?: string }) => (
  <g className={className}>
    <rect className="ga-bar" x="56" y="146" width="128" height="32" rx="6" />
    <ActionButton x={62} y={150} tone="success" icon="check" />
    <ActionButton x={92} y={150} tone="primary" icon="tag" />
    <ActionButton x={122} y={150} tone="warning" icon="ignore" />
    <ActionButton x={152} y={150} tone="error" icon="x" />
  </g>
);

// Approve: orange For Review badge gives way to a green border and check.
export const ApproveScene = () => (
  <Scene label="Approving a suggested label" className={styles.reviewScene}>
    <Tile x={70} y={28} size={100} />
    <Badge
      x={76}
      y={34}
      width={62}
      text="For Review"
      tone="warning"
      className="ga-approve-badge"
    />
    <rect
      className="ga-stroke-success ga-approve-border"
      x="70"
      y="28"
      width="100"
      height="100"
      rx="16"
      strokeWidth="3"
    />
    <g className="ga-pop ga-approve-check">
      <circle className="ga-fill-success" cx="160" cy="38" r="11" />
      <path
        className="ga-check-draw ga-approve-check-path"
        d="M154 38l4 4l8 -8"
      />
    </g>
    <ReviewActionBar />
    <circle
      className="ga-stroke-success ga-ripple ga-approve-ripple"
      cx="74"
      cy="162"
      r="8"
      strokeWidth="2"
    />
    <Cursor className="ga-cur-approve" />
  </Scene>
);

// Reassign, ignore and decline shown on three tiles, one after another.
export const OtherActionsScene = () => (
  <Scene
    label="Reassign, ignore and decline actions"
    className={styles.reviewScene}
  >
    <g className="ga-oa-tile1">
      <Tile x={24} y={40} size={56} />
      <Badge
        x={28}
        y={44}
        width={44}
        text="Particle"
        tone="success"
        className="ga-oa-chip-a"
      />
      <Badge
        x={28}
        y={44}
        width={44}
        text="Scratch"
        tone="primary"
        className="ga-oa-chip-b"
      />
    </g>
    <g className="ga-oa-tile2">
      <Tile x={92} y={40} size={56} />
      <Badge
        x={96}
        y={44}
        width={44}
        text="Ignore"
        tone="muted"
        className="ga-oa-ignore-badge"
      />
    </g>
    <g className="ga-oa-tile3">
      <Tile x={160} y={40} size={56} />
      <rect
        className="ga-stroke-error ga-oa-decline-border"
        x="160"
        y="40"
        width="56"
        height="56"
        rx="9"
        strokeWidth="2.5"
      />
      <g className="ga-oa-decline-note">
        <rect
          className="ga-surface"
          x="130"
          y="10"
          width="112"
          height="22"
          rx="7"
        />
        <path className="ga-surface" d="M184 32l4 5l4 -5z" />
        <text
          className="ga-text"
          x="186"
          y="24.5"
          fontSize="7.5"
          textAnchor="middle"
        >
          Moved to next suggestion
        </text>
      </g>
    </g>

    <ActionButton x={40} y={112} tone="primary" icon="tag" />
    <ActionButton x={108} y={112} tone="warning" icon="ignore" />
    <ActionButton x={176} y={112} tone="error" icon="x" />
    <text
      className="ga-text ga-muted"
      x="52"
      y="152"
      fontSize="8.5"
      textAnchor="middle"
    >
      Reassign
    </text>
    <text
      className="ga-text ga-muted"
      x="120"
      y="152"
      fontSize="8.5"
      textAnchor="middle"
    >
      Ignore
    </text>
    <text
      className="ga-text ga-muted"
      x="188"
      y="152"
      fontSize="8.5"
      textAnchor="middle"
    >
      Decline
    </text>

    <circle
      className="ga-stroke-primary ga-ripple ga-oa-ripple1"
      cx="52"
      cy="124"
      r="8"
      strokeWidth="2"
    />
    <circle
      className="ga-ripple ga-oa-ripple2"
      cx="120"
      cy="124"
      r="8"
      strokeWidth="2"
      fill="none"
      stroke="var(--primary-accent-color-warning)"
    />
    <circle
      className="ga-stroke-error ga-ripple ga-oa-ripple3"
      cx="188"
      cy="124"
      r="8"
      strokeWidth="2"
    />
    <Cursor className="ga-cur-oa" />
  </Scene>
);

const SUBMIT_TILES = [40, 92, 144];

// Finish the batch, then Submit lights up and gets clicked.
export const SubmitScene = () => (
  <Scene
    label="Reviewing the last images and submitting the round"
    className={styles.reviewScene}
  >
    <rect className="ga-surface" x="20" y="20" width="200" height="24" rx="8" />
    <text
      className="ga-text ga-muted"
      x="44"
      y="36"
      fontSize="9"
      textAnchor="middle"
    >
      All
    </text>
    <rect
      className="ga-fill-primary"
      x="70"
      y="24"
      width="72"
      height="16"
      rx="6"
      opacity="0.25"
    />
    <text className="ga-text" x="106" y="36" fontSize="9" textAnchor="middle">
      For Review
    </text>
    <text
      className="ga-text ga-muted"
      x="180"
      y="36"
      fontSize="9"
      textAnchor="middle"
    >
      Annotated
    </text>

    {SUBMIT_TILES.map((x, i) => (
      <g key={x}>
        <Tile x={x} y={60} size={48} />
        <Badge
          x={x + 3}
          y={63}
          width={42}
          text="Review"
          tone="warning"
          className={`ga-submit-badge ga-submit-badge-${i + 1}`}
        />
        <rect
          className={`ga-stroke-success ga-submit-border ga-submit-border-${i + 1}`}
          x={x}
          y="60"
          width="48"
          height="48"
          rx="8"
          strokeWidth="2.5"
        />
      </g>
    ))}

    <text className="ga-text ga-muted" x="20" y="130" fontSize="8.5">
      Total Review Progress
    </text>
    <rect className="ga-bar" x="20" y="136" width="200" height="5" rx="2.5" />
    <rect
      className="ga-fill-primary ga-submit-progress"
      x="20"
      y="136"
      width="90"
      height="5"
      rx="2.5"
    />

    <rect
      className="ga-btn-disabled ga-submit-off"
      x="70"
      y="156"
      width="100"
      height="28"
      rx="8"
    />
    <rect
      className="ga-fill-primary ga-submit-on"
      x="70"
      y="156"
      width="100"
      height="28"
      rx="8"
    />
    <text
      className="ga-text"
      x="120"
      y="174"
      fontSize="10.5"
      textAnchor="middle"
    >
      Submit
    </text>
    <circle
      className="ga-stroke-primary ga-ripple ga-submit-ripple"
      cx="120"
      cy="170"
      r="8"
      strokeWidth="2"
    />
    <Cursor className="ga-cur-submit" />
  </Scene>
);

// Ring fills to 100 percent and the goal is reached.
export const GoalScene = () => (
  <Scene
    label="Train-Worthy Goal reaching 100 percent"
    className={styles.reviewScene}
  >
    <circle className="ga-outline" cx="120" cy="86" r="46" strokeWidth="8" />
    <circle
      className="ga-stroke-success ga-goal-ring"
      cx="120"
      cy="86"
      r="46"
      strokeWidth="8"
      strokeLinecap="round"
      strokeDasharray="289"
      strokeDashoffset="289"
      transform="rotate(-90 120 86)"
    />
    <text
      className="ga-text ga-goal-pct-running"
      x="120"
      y="92"
      fontSize="18"
      textAnchor="middle"
    >
      68%
    </text>
    <text
      className="ga-text ga-goal-pct-done"
      x="120"
      y="92"
      fontSize="18"
      textAnchor="middle"
    >
      100%
    </text>
    <g className="ga-pop ga-goal-check">
      <circle className="ga-fill-success" cx="156" cy="52" r="12" />
      <path
        className="ga-check-draw ga-goal-check-path"
        d="M149.5 52l4.5 4.5l8.5 -9"
      />
    </g>
    <text
      className="ga-text"
      x="120"
      y="156"
      fontSize="10.5"
      textAnchor="middle"
    >
      Train-Worthy Goal
    </text>
    <text
      className="ga-text ga-muted"
      x="120"
      y="172"
      fontSize="8.5"
      textAnchor="middle"
    >
      200 labeled images per label
    </text>
  </Scene>
);
